/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const tibbit = project.getFeature(feature.getSettingValue('TIBBIT'));
    if (!tibbit) {
        return blocks;
    }
    const tibbitName = tibbit.getSettingValue('NAME');
    const functionName = `${tibbitName}_data_available_${feature.getID()}`;
    const SERNAME = `SER_${tibbit.getSettingValue('NAME')}`;
    const dataLocation = `${SERNAME}_data`;

    const links = project.getNodeLinks(feature, 'data_available');
    if (links.length > 0) {
        let dataCode = '';
        const link = links[0];
        dataCode += `'****${link.getSourceId()}****'\r\n`;
        blocks.push({
            location: `on_${SERNAME}_data_arrival`,
            blockType: BlockType.CODE,
            template: `${functionName}(${dataLocation})`,
        });

        const format = feature.getSettingValue('FORMAT');
        let formattingCode = '';
        if (format === 'hex') {
            formattingCode = `
    facility_code=math_bin_to_hex(facility_code)
    card_code=math_bin_to_hex(card_code)
    id_code=math_bin_to_hex(id_code)
    wiegand_data=math_bin_to_hex(wiegand_data_raw)
    `;
        } else if (format === 'dec') {
            formattingCode = `
    facility_code=lval("&b" + facility_code)
    card_code=lval("&b" + card_code)
    id_code=lval("&b" + id_code)
    wiegand_data=lval("&b" + wiegand_data)
    `;
        }
        blocks.push({
            location: 'DEVICE_TBS',
            id: functionName,
            blockType: BlockType.LOCATION,
            template: `
sub ${functionName}(wiegand_data_raw as string)

    dim facility_code as string
    dim card_code as string
    dim wiegand_data as string
    dim id_code as string
    wiegand_data=wiegand_data_raw
    wiegand_process_input(wiegand_data_raw, facility_code, card_code, id_code)
    ${formattingCode}
    ${dataCode}
end sub
            `,
        });
        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            id: `declare_${functionName}`,
            blockType: BlockType.CODE,
            template: `
'Tibbit 08 data available callback for ${tibbitName}
declare sub ${functionName}(wiegand_data_raw as string)`,
        });
    }

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        id: 'declare_wiegand_process_input',
        template: `
declare function math_bin_to_hex(byref binary_str as string) as string            
declare sub wiegand_process_input(byref input as string, byref facility_code as string, byref card_code as string, byref id_code as string)`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        id: 'wiegand_process_input',
        template: `
function math_bin_to_hex(byref binary_str as string) as string
    dim hex_result as string
    dim expected_hex_length as byte
    
    ' Skip if empty input
    if len(binary_str) = 0 then
        math_bin_to_hex = ""
        exit function
    end if
    
    ' Calculate expected hex length (4 bits per hex character, rounded up)
    expected_hex_length = (len(binary_str) + 3) / 4
    
    ' Convert binary to hex
    hex_result = lhex(lval("&b" + binary_str))
    
    ' Remove "0x" or "&h" prefix if present
    if left(hex_result, 2) = "0x" or left(hex_result, 2) = "&h" then
        hex_result = right(hex_result, len(hex_result) - 2)
    end if
    
    ' Zero pad to expected length
    if len(hex_result) < expected_hex_length then
        hex_result = strgen(expected_hex_length - len(hex_result), "0") + hex_result
    end if
    
    math_bin_to_hex = hex_result
end function

sub wiegand_process_input(byref input as string, byref facility_code as string, byref card_code as string, byref id_code as string)
    dim bit_count as byte=len(input)
    dim facility_code_count as byte=8
    select case bit_count
        case 26:
            facility_code_count=8
        case 32:
            facility_code_count=10
        case 34:
            facility_code_count=12
        case 37:
            facility_code_count=15
        case 40:
            facility_code_count=16
        case 42:
            facility_code_count=18
        case 56:
            facility_code_count=24
        case 58:
            facility_code_count=26
        case 64:
            facility_code_count=30
        case else:

    end select
    facility_code=mid(input,2,facility_code_count)
    card_code=mid(input,2+facility_code_count,bit_count-facility_code_count-2)
    id_code=mid(input,2,facility_code_count+len(card_code))
end sub`,
    });


    return blocks;
};
